// ============================================================================
// stage1-image-text-routing.ts: send each imported image to the right lane
// ----------------------------------------------------------------------------
// Peter, 2026-10-13: "Make sure to process jpg and image files that may be the
// RFQ. Currently it doesn't."
//
// Before this, Stage 1 sent every image to the photo lane only, so an RFQ that
// arrived as a photo, a scan or a screenshot never reached RFQ extraction,
// document classification or the Summary. This decides, per image, from its
// CONTENT only: the content sort (triage) and the length of the text read.
// The file name decides nothing (Peter, 2026-10-13: "file names must not
// decide anything, only the contents"):
//
//   document  sorted as a document, 80+ characters of text read
//                                            -> documents[] only (via the
//             existing image branch of handleDocsFiles, so it is classified
//             and can be the RFQ)
//   both      a site photo with writing      -> photos[] AND a derived text
//             row in documents[]
//   photo     no text, too little text, the AI call failed, the image could
//             not be decoded, or the run was stopped -> photos[] as before
//
// No image is ever dropped: every image ends in documents[] or photos[].
// At most 2 OCR calls and 2 triage calls run at once. No timeouts on AI calls.
// ============================================================================

import {
    chunkForTriage,
    isHeicLike,
    makeTriageThumbnail,
    triageImageBatch,
    type ImageTextKind,
} from './stage1-image-text-triage';
import {
    MIN_DOCUMENT_TEXT_CHARS,
    mapWithConcurrency,
    readTextFromImage,
    type ImageSource,
    type ImageTextReadResult,
} from './image-text-reading';

export const IMAGE_TEXT_OCR_CONCURRENCY = 2;
export const IMAGE_TEXT_TRIAGE_CONCURRENCY = 2;
export const IMAGE_TEXT_THUMBNAIL_CONCURRENCY = 3;
/** A "both" photo needs at least this much text (a measurement like "2400"). */
export const MIN_PHOTO_TEXT_CHARS = 3;

export type ImageTextVerdict =
    | 'document'
    | 'both'
    | 'photo'
    | 'photo-no-text'
    | 'photo-little-text'
    | 'photo-read-failed'
    | 'photo-unreadable'
    | 'photo-not-checked';

export type ImageTextFeedTone = 'info' | 'document' | 'photo' | 'warning';

export interface Stage1ImageFile {
    name: string;
    type?: string;
}

export interface Stage1ImageTextRouting<F> {
    /** Images whose text makes them documents, in import order. */
    documentFiles: F[];
    /** Images that stay photos (including "both"), in import order. */
    photoFiles: F[];
    /** Photos that also get a derived text row. */
    bothFiles: Set<F>;
    /** Text read per image ("document" and "both" only). */
    textByFile: Map<F, string>;
    verdicts: Map<F, ImageTextVerdict>;
}

export interface Stage1ImageTextRoutingDeps {
    apiKey?: string | null;
    model?: string | null;
    projectId?: string | null;
    shouldStop?: () => boolean;
    onFeed?: (text: string, tone: ImageTextFeedTone) => void;
    /** Test seams; production uses the real services. */
    makeThumbnail?: (file: ImageSource) => Promise<string | null>;
    triageBatch?: (args: { thumbnails: string[]; apiKey: string; model?: string | null; projectId?: string | null }) => Promise<ImageTextKind[]>;
    readText?: (args: { image: ImageSource; fileName: string; apiKey: string; model?: string | null; projectId?: string | null }) => Promise<ImageTextReadResult>;
}

function plural(count: number, word: string): string {
    return `${count} ${word}${count === 1 ? '' : 's'}`;
}

export async function routeStage1ImagesByText<F extends Stage1ImageFile & ImageSource>(
    files: readonly F[],
    deps: Stage1ImageTextRoutingDeps = {},
): Promise<Stage1ImageTextRouting<F>> {
    const shouldStop = deps.shouldStop || (() => false);
    const feed = (text: string, tone: ImageTextFeedTone) => {
        try { deps.onFeed?.(text, tone); } catch { /* the feed is display only */ }
    };
    const makeThumbnail = deps.makeThumbnail || makeTriageThumbnail;
    const triageBatch = deps.triageBatch || triageImageBatch;
    const readText = deps.readText || readTextFromImage;

    const verdicts = new Map<F, ImageTextVerdict>();
    const textByFile = new Map<F, string>();
    const bothFiles = new Set<F>();
    files.forEach(file => verdicts.set(file, 'photo-not-checked'));

    const finish = (): Stage1ImageTextRouting<F> => ({
        documentFiles: files.filter(file => verdicts.get(file) === 'document'),
        photoFiles: files.filter(file => verdicts.get(file) !== 'document'),
        bothFiles,
        textByFile,
        verdicts,
    });

    if (files.length === 0 || !deps.apiKey || shouldStop()) return finish();
    const apiKey = deps.apiKey;

    // 1. A small copy of each image. It doubles as the decode check: an image
    //    this browser cannot open (HEIC on most desktops) keeps the photo lane.
    const thumbnails = new Map<F, string>();
    await mapWithConcurrency(files, IMAGE_TEXT_THUMBNAIL_CONCURRENCY, async (file) => {
        let thumbnail: string | null = null;
        try { thumbnail = await makeThumbnail(file); } catch { thumbnail = null; }
        if (thumbnail) {
            thumbnails.set(file, thumbnail);
            return;
        }
        verdicts.set(file, 'photo-unreadable');
        // The decode failure decided the route; the type only words the notice.
        feed(isHeicLike(file)
            ? `${file.name}: this browser cannot open HEIC images, so its text was not read. It is kept as a photo.`
            : `${file.name}: the image could not be opened to read its text. It is kept as a photo.`, 'warning');
    }, shouldStop);
    if (shouldStop()) return finish();

    // 2. Every image that opened is sorted by its content, one call per 12
    //    images. Its file name plays no part.
    const kinds = new Map<F, ImageTextKind>();
    const toTriage: F[] = files.filter(file => thumbnails.has(file));
    if (toTriage.length > 0) {
        feed(`Checking ${plural(toTriage.length, 'image')} for text`, 'info');
        let failedBatches = 0;
        await mapWithConcurrency(chunkForTriage(toTriage), IMAGE_TEXT_TRIAGE_CONCURRENCY, async (batch) => {
            let batchKinds: ImageTextKind[] = [];
            try {
                batchKinds = await triageBatch({
                    thumbnails: batch.map(file => thumbnails.get(file) as string),
                    apiKey,
                    model: deps.model,
                    projectId: deps.projectId,
                });
            } catch (err: any) {
                failedBatches += 1;
                console.warn('[ImageText] triage call failed; those images stay photos:', err?.message || err);
            }
            batch.forEach((file, i) => kinds.set(file, batchKinds[i] || 'photo'));
        }, shouldStop);
        if (failedBatches > 0) {
            feed(`Could not check ${plural(failedBatches, 'group')} of images for text. Those images are kept as photos.`, 'warning');
        }
    }
    if (shouldStop()) return finish();
    for (const file of files) {
        if (kinds.get(file) === 'photo') verdicts.set(file, 'photo');
    }

    // 3. Read the text of every image that may carry some.
    const candidates = files.filter(file => kinds.get(file) === 'document' || kinds.get(file) === 'both');
    if (candidates.length === 0) return finish();
    feed(`Reading text in ${plural(candidates.length, 'image')}`, 'info');
    await mapWithConcurrency(candidates, IMAGE_TEXT_OCR_CONCURRENCY, async (file) => {
        let reading: ImageTextReadResult;
        try {
            reading = await readText({ image: file, fileName: file.name, apiKey, model: deps.model, projectId: deps.projectId });
        } catch (err: any) {
            reading = { status: 'failed', text: '', reason: String(err?.message || 'unknown error') };
        }
        if (shouldStop()) return;
        const text = reading.status === 'text' ? String(reading.text || '') : '';
        const length = text.trim().length;
        if (kinds.get(file) === 'both') {
            if (length >= MIN_PHOTO_TEXT_CHARS) {
                verdicts.set(file, 'both');
                bothFiles.add(file);
                textByFile.set(file, text);
                feed(`${file.name}: text found, kept as a photo and added to documents`, 'photo');
            } else {
                verdicts.set(file, reading.status === 'failed' ? 'photo-read-failed' : 'photo-no-text');
            }
            return;
        }
        if (length >= MIN_DOCUMENT_TEXT_CHARS) {
            verdicts.set(file, 'document');
            textByFile.set(file, text);
            feed(`${file.name}: text found, added to documents`, 'document');
            return;
        }
        if (reading.status === 'failed') {
            verdicts.set(file, 'photo-read-failed');
            feed(`${file.name}: its text could not be read. It is kept as a photo.`, 'warning');
        } else if (length > 0) {
            verdicts.set(file, 'photo-little-text');
            feed(`${file.name}: little text found, kept as a photo`, 'photo');
        } else {
            verdicts.set(file, 'photo-no-text');
            feed(`${file.name}: no text found, kept as a photo`, 'photo');
        }
    }, shouldStop);

    return finish();
}
